import jsPDF from "jspdf";

export type EducationDetail = {
    id: string;
    institution: string;
    degree: string;
    fieldOfStudy: string;
    startYear: string;
    endYear: string;
    grade: string;
};

export type ExperienceDetail = {
    id: string;
    title: string;
    organization: string;
    type: "work" | "internship";
    startDate: string;
    endDate: string;
    current: boolean;
    description: string;
};

export type CertificationDetail = {
    id: string;
    name: string;
    issuer: string;
    issueDate: string;
    credentialUrl: string;
};

export type ProfessionalProfileData = {
    name: string;
    email: string;
    location: string;
    linkedinUrl: string;
    headline: string;
    bio: string;
    careerAmbition: string;
    skills: string[];
    education: EducationDetail[];
    experience: ExperienceDetail[];
    certifications: CertificationDetail[];
};

const text = (value: string) => value.replace(/[^ -~\n]/g, "-").replace(/\s+\n/g, "\n").trim();
const complete = (value: string) => Boolean(value.trim());

const month = (value: string) => {
    if (!value) return "";
    const [year, part] = value.split("-");
    if (!part) return year;
    return new Date(Number(year), Number(part) - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

export function createProfessionalProfilePdf(profile: ProfessionalProfileData) {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();
    const left = 18;
    const right = width - 18;
    const contentWidth = right - left;
    let y = 18;
    const paintPage = () => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, width, height, "F");
    };
    paintPage();

    const ensure = (needed: number) => {
        if (y + needed <= height - 16) return;
        pdf.addPage();
        paintPage();
        y = 18;
    };
    const lines = (value: string, size = 10) => {
        pdf.setFontSize(size);
        return pdf.splitTextToSize(text(value), contentWidth) as string[];
    };
    const section = (title: string) => {
        ensure(14);
        y += 4;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(7, 67, 145);
        pdf.text(title.toUpperCase(), left, y);
        y += 2.5;
        pdf.setDrawColor(148, 163, 184);
        pdf.setLineWidth(0.25);
        pdf.line(left, y, right, y);
        y += 6;
    };
    const paragraph = (value: string) => {
        if (!complete(value)) return;
        const wrapped = lines(value);
        ensure(wrapped.length * 4.7 + 2);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(30, 41, 59);
        pdf.text(wrapped, left, y);
        y += wrapped.length * 4.7 + 2;
    };

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(24);
    pdf.setTextColor(6, 26, 74);
    pdf.text(text(profile.name || "Student Profile"), left, y);
    y += 8;
    if (profile.headline) {
        pdf.setFontSize(11);
        pdf.setTextColor(7, 93, 204);
        pdf.text(lines(profile.headline, 11), left, y);
        y += lines(profile.headline, 11).length * 4.8;
    }
    const contact = [profile.email, profile.location, profile.linkedinUrl].filter(complete).map(text).join("  |  ");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(71, 85, 105);
    const contactLines = pdf.splitTextToSize(contact, contentWidth) as string[];
    pdf.text(contactLines, left, y);
    y += contactLines.length * 4 + 2;

    if (profile.bio) { section("Professional summary"); paragraph(profile.bio); }
    if (profile.skills.length) {
        section("Core skills");
        paragraph(profile.skills.map(text).join(" | "));
    }
    if (profile.experience.length) {
        section("Experience");
        profile.experience.forEach((item) => {
            const organizationLines = lines(item.organization, 9.5);
            ensure(11 + organizationLines.length * 4.3);
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(10.5); pdf.setTextColor(15, 23, 42);
            pdf.text(text(item.title || (item.type === "internship" ? "Internship" : "Role")), left, y);
            const period = [month(item.startDate), item.current ? "Present" : month(item.endDate)].filter(Boolean).join(" - ");
            if (period) { pdf.setFontSize(8.5); pdf.setTextColor(71, 85, 105); pdf.text(text(period), right, y, { align: "right" }); }
            y += 4.8;
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(9.5); pdf.setTextColor(51, 65, 85); pdf.text(organizationLines, left, y); y += organizationLines.length * 4.3;
            paragraph(item.description);
        });
    }
    if (profile.education.length) {
        section("Education");
        profile.education.forEach((item) => {
            const educationLines = lines([item.institution, item.grade].filter(complete).join(" | "), 9.5);
            ensure(9 + educationLines.length * 4.3);
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(10.5); pdf.setTextColor(15, 23, 42);
            pdf.text(text([item.degree, item.fieldOfStudy].filter(complete).join(" in ")), left, y);
            pdf.setFontSize(8.5); pdf.setTextColor(71, 85, 105);
            pdf.text(text([item.startYear, item.endYear].filter(complete).join(" - ")), right, y, { align: "right" });
            y += 4.8;
            pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5); pdf.setTextColor(51, 65, 85);
            pdf.text(educationLines, left, y); y += educationLines.length * 4.3 + 2;
        });
    }
    if (profile.certifications.length) {
        section("Certifications");
        profile.certifications.forEach((item) => {
            const detailLines = lines([item.issuer, month(item.issueDate), item.credentialUrl].filter(complete).join(" | "), 9);
            ensure(detailLines.length * 4.1 + 7);
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); pdf.setTextColor(15, 23, 42); pdf.text(text(item.name), left, y);
            pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(71, 85, 105);
            pdf.text(detailLines, left, y + 4.5); y += detailLines.length * 4.1 + 6;
        });
    }
    if (profile.careerAmbition) { section("Career objective"); paragraph(profile.careerAmbition); }

    const pages = pdf.getNumberOfPages();
    for (let page = 1; page <= pages; page += 1) {
        pdf.setPage(page); pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5); pdf.setTextColor(100, 116, 139);
        pdf.text(`MockZo professional profile | Page ${page} of ${pages}`, width / 2, height - 7, { align: "center" });
    }
    return pdf;
}
